import { logger } from '@adornis/base/logging.js';
import { getLLMRouter } from '../llm/llm-gate.js';
import { LLM_PAYLOAD_SAFE_LIMIT_BYTES } from '../../../shared/constants.js';
import { taskExtractionConfidenceModel, taskExtractionConfidenceTemperature } from '../../../shared/models.js';
import type { AdditionalInfo, DescriptionWarningCode } from '../../exam.js';
import { addCostToExam } from '../add-cost.js';
import {
  additionalInfoImageIds,
  formatAdditionalInfoText,
  normalizeAdditionalInfoForPrompt,
  resolveAdditionalInfoForTask,
  type AdditionalInfoPromptItem,
} from './additional-info-context.js';
import {
  assembleHierarchyPayload,
  clampPayloadToByteLimit,
  imageIdsAsBoxes,
  type HierarchyTaskInput,
  type PayloadBox,
} from './agent-payload.js';
import type { RecursiveExtractionTask } from './extraction-modules/task-enrichment.js';

export interface TaskExtractionSemanticResult {
  score: number;
  reason?: string;
  warningCodes?: DescriptionWarningCode[];
}

export interface CalculateTaskExtractionConfidenceParams {
  bbxConfidence?: number;
  ocrConfidence?: number;
  baseConfidenceOverride?: number;
  extractedText: string;
  unreadableArtifactThreshold?: number;
}

export interface ApplySemanticConfidenceToRecursiveTasksParams {
  tasks: RecursiveExtractionTask[];
  additionalInformation?: AdditionalInfo[];
  onCost?: (cost: number) => void;
  unreadableArtifactThreshold?: number;
  semanticConcurrency?: number;
}

export interface TaskExtractionConfidenceResult {
  baseConfidence: number;
  finalConfidence: number;
  artifactDensity: number;
  semanticScore: number;
  semanticReason?: string;
  skippedSemantic: boolean;
  warningCodes?: DescriptionWarningCode[];
}

const DEFAULT_UNREADABLE_ARTIFACT_THRESHOLD = 0.4; // Treshold for skipping semantic evaluation of the task extraction e.g. extractedText contains 50% signs like -, /, * skip
function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

function normalizeText(value: string): string {
  return String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim();
}

// Characters that strongly correlate with broken OCR or column-mix artifacts.
const ARTIFACT_CHAR_REGEX = /[|\\~^`¦§¤¬«»°_]/g;

export function calculateArtifactDensity(rawText: string): number {
  const text = normalizeText(rawText);
  if (!text) return 1;

  const chars = Array.from(text).filter(ch => !/\s/.test(ch));
  if (chars.length === 0) return 1;

  const artifactChars = (text.match(ARTIFACT_CHAR_REGEX) || []).length;
  return clamp01(artifactChars / chars.length);
}

export function calculateBaseExtractionConfidence(bbxConfidence: number | undefined, ocrConfidence: number): number {
  const bbx = typeof bbxConfidence === 'number' ? clamp01(bbxConfidence) : 1;
  const ocr = clamp01(ocrConfidence);
  return clamp01(bbx * ocr);
}

function extractResponseText(raw: unknown): string {
  if (typeof raw === 'string') return raw;
  if (Array.isArray(raw)) {
    return raw
      .map((item: any) => (typeof item?.text === 'string' ? item.text : typeof item === 'string' ? item : ''))
      .join('')
      .trim();
  }
  return '';
}

async function runWithConcurrency<T>(
  items: T[],
  concurrency: number,
  worker: (item: T) => Promise<void>,
): Promise<void> {
  if (items.length === 0) return;

  const cappedConcurrency = Math.max(1, Math.floor(concurrency));
  let nextIndex = 0;

  const runWorker = async () => {
    while (true) {
      const currentIndex = nextIndex;
      nextIndex += 1;
      if (currentIndex >= items.length) return;
      await worker(items[currentIndex] as T);
    }
  };

  const workers = Array.from({ length: Math.min(cappedConcurrency, items.length) }, () => runWorker());
  await Promise.all(workers);
}

const TREE_SEMANTIC_SYSTEM_PROMPT = `You are an expert exam reviewer evaluating the quality of OCR-extracted exam tasks and whether they are solvable as-is.

You are given a hierarchical set of exam tasks. Each task is introduced by a header "========== TASK \\"ID\\" ==========".
You must evaluate ONLY the tasks whose IDs are listed as evaluation targets (provided at the start of the user message).
All other tasks in the hierarchy are context only — they give you the broader exam context to better judge the targets.

For each target task, determine whether a teacher could grade a student's answer using ONLY the task description (text and images shown), plus the context of ancestor tasks visible above it. DO NOT make any inferential leaps, fill in the blanks, or assume implied instructions.

Evaluate based on 4 criteria:
1. Actionability: Is there a clear instruction or question? Purely informational context without a task should be penalized.
2. Coherence: Does the text make grammatical and logical sense? Penalize heavily if it looks like two unrelated columns of text were merged together.
3. Visual Data Verification (CRITICAL FOR IMAGES): If the task requires external visual materials (e.g., "Graph", "Figure 1") or if data is missing from the text, you MUST check the provided images. The images must clearly and legibly contain the specific data needed to solve the task. If the image is unrelated, cut off, or lacks the necessary data points, the task is unsolvable.
4. Text-Based Data Integrity: If NO image is provided, and the task relies on the text for graph/table data, the specific values MUST be explicitly paired (e.g., "Oslo: -4°C"). If the text only provides disconnected lists of numbers and labels, it is UNSOLVABLE.

Scoring Rubric (0.0 to 1.0):
- 0.9 to 1.0: Perfect. Clear action, coherent, and all required data is perfectly legible in the provided images OR explicitly paired in the text.
- 0.6 to 0.8: Minor issues. Understandable despite slight OCR typos or slightly blurry but readable images.
- 0.3 to 0.5: Major issues. Crucial context is missing, or the provided images only partially cover the required data.
- 0.0 to 0.2: Unsolvable. Missing required data, garbled text, OR the provided images do not contain the data needed to solve the question.

Return STRICT JSON with this schema only. Do not use markdown formatting, markdown code blocks, or extra keys:
{
  "results": [
    {
      "taskId": "the task ID exactly as provided in the evaluation targets",
      "score": number,
      "reason": "Short, specific explanation for the score",
      "warningCodes": []
    }
  ]
}

For "warningCodes", include zero or more of these values when the issue applies:
- "text_cutoff": the task text appears cut off or the question is incomplete
- "missing_information": specific values or data required to solve the task are absent from text and images
- "image_unreadable": a required image is missing, cut off, or too blurry to read the needed data

Include exactly one result entry per evaluation target.`;

function toDescriptionPayloadBoxes(boxes: any[] | undefined): PayloadBox[] {
  return (Array.isArray(boxes) ? boxes : []).map((box: any) => ({
    contentType: box?.contentType ?? null,
    ocrExtractedText: box?.ocrExtractedText ?? null,
    imageId: box?.imageId ?? null,
    isHandwritten: box?.isHandwritten ?? null,
  }));
}

function collectAllAdditionalInfoIds(tasks: RecursiveExtractionTask[]): Set<string> {
  const ids = new Set<string>();
  const walk = (task: RecursiveExtractionTask) => {
    for (const rawId of Array.isArray(task.additionalInfoIds) ? task.additionalInfoIds : []) {
      const id = String(rawId ?? '').trim();
      if (id) ids.add(id);
    }
    for (const subtask of Array.isArray(task.subtasks) ? task.subtasks : []) {
      walk(subtask);
    }
  };
  for (const task of tasks) walk(task);
  return ids;
}

interface RecursiveTaskNodeInfo {
  task: RecursiveExtractionTask;
  id: string;
  isTarget: boolean;
  additionalInfoItems: AdditionalInfoPromptItem[];
}

function collectRecursiveNodes(
  task: RecursiveExtractionTask,
  id: string,
  allAdditionalInfo: AdditionalInfoPromptItem[],
  allTaskAdditionalInfoIds: Set<string>,
): RecursiveTaskNodeInfo[] {
  const nodes: RecursiveTaskNodeInfo[] = [];

  const hasNonImageBoxes =
    Array.isArray(task.descriptionBoundingBoxes) &&
    task.descriptionBoundingBoxes.some((box: any) => box?.contentType !== 'image');
  const isTarget = (task as any).needsAnswer !== false && hasNonImageBoxes;

  nodes.push({
    task,
    id,
    isTarget,
    additionalInfoItems: resolveAdditionalInfoForTask(task, allAdditionalInfo, allTaskAdditionalInfoIds),
  });

  for (const subtask of Array.isArray(task.subtasks) ? (task.subtasks as RecursiveExtractionTask[]) : []) {
    nodes.push(
      ...collectRecursiveNodes(subtask, `${id}/${subtask.taskNumber}`, allAdditionalInfo, allTaskAdditionalInfoIds),
    );
  }

  return nodes;
}

async function evaluateTaskTreeSemanticLLM(
  hierarchyTasks: HierarchyTaskInput[],
  targetIds: string[],
  options?: { onCost?: (cost: number) => void },
  examid?: string,
): Promise<Map<string, TaskExtractionSemanticResult>> {
  const router = getLLMRouter();

  const contextParts = await clampPayloadToByteLimit(
    await assembleHierarchyPayload(hierarchyTasks, { imageDetail: 'low' }),
    LLM_PAYLOAD_SAFE_LIMIT_BYTES,
  );

  const userContentParts: any[] = [
    {
      type: 'text',
      text: `Evaluate the OCR extraction quality for the following exam task hierarchy.\n\nEVALUATION TARGETS (evaluate only these tasks): ${JSON.stringify(targetIds)}`,
    },
    ...contextParts,
  ];

  let response: any;
  try {
    response = await router.chat.completions.create({
      model: taskExtractionConfidenceModel,
      provider: 'openrouter',
      temperature: taskExtractionConfidenceTemperature,
      messages: [
        { role: 'system', content: TREE_SEMANTIC_SYSTEM_PROMPT },
        { role: 'user', content: userContentParts },
      ],
      response_format: { type: 'json_object' },
    });
  } catch (error) {
    logger.error(
      { err: error, examid, context: 'evaluateTaskTreeSemanticLLM' },
      'Tree semantic evaluation LLM call failed',
    );
    throw error;
  }

  const usageCost = Number((response?.usage as any)?.cost ?? 0);
  if (options?.onCost && Number.isFinite(usageCost) && usageCost > 0) {
    options.onCost(usageCost);
  } else {
    await addCostToExam({ usage: { cost: usageCost } }, examid, 'taskExtractionConfidence');
  }
  logger.info({ usageCost, context: 'COST_TRACKING' }, 'taskExtractionConfidence tree agent cost');

  const raw = extractResponseText(response?.choices?.[0]?.message?.content);
  const results = new Map<string, TaskExtractionSemanticResult>();

  const VALID_WARNING_CODES = new Set<DescriptionWarningCode>([
    'text_cutoff',
    'missing_information',
    'image_unreadable',
  ]);

  try {
    const parsed = JSON.parse(raw);
    for (const item of Array.isArray(parsed?.results) ? parsed.results : []) {
      const taskId = String(item?.taskId ?? '').trim();
      if (!taskId) continue;
      const score = clamp01(Number(item?.score ?? 0));
      const reason = String(item?.reason ?? '').trim() || undefined;
      const warningCodes = Array.isArray(item?.warningCodes)
        ? (item.warningCodes.filter(
            (c: unknown) => typeof c === 'string' && VALID_WARNING_CODES.has(c as DescriptionWarningCode),
          ) as DescriptionWarningCode[])
        : undefined;
      logger.info({ taskId, score, reason, warningCodes }, 'Tree semantic evaluation result for task');
      results.set(taskId, { score, reason, warningCodes });
    }
  } catch {
    // return empty map; callers treat missing results as score=1 fallback
  }

  return results;
}

export async function applySemanticConfidenceToRecursiveTasks(
  params: ApplySemanticConfidenceToRecursiveTasksParams,
): Promise<RecursiveExtractionTask[]> {
  const {
    tasks,
    additionalInformation,
    onCost,
    unreadableArtifactThreshold = DEFAULT_UNREADABLE_ARTIFACT_THRESHOLD,
    semanticConcurrency = 6,
  } = params;

  if (!Array.isArray(tasks) || tasks.length === 0) return tasks;

  const allAdditionalInfo = normalizeAdditionalInfoForPrompt(additionalInformation);
  const allTaskAdditionalInfoIds = collectAllAdditionalInfoIds(tasks);

  const taskWarningCodes = new Map<RecursiveExtractionTask, Set<DescriptionWarningCode>>();

  await runWithConcurrency(tasks, semanticConcurrency, async rootTask => {
    try {
      const nodes = collectRecursiveNodes(rootTask, rootTask.taskNumber, allAdditionalInfo, allTaskAdditionalInfoIds);

      const targetIds = nodes.filter(n => n.isTarget).map(n => n.id);
      if (targetIds.length === 0) return;

      const hierarchyInputs: HierarchyTaskInput[] = nodes.map((node, index) => ({
        id: node.id,
        hierarchy: index === 0 ? 'MAIN' : 'SUB',
        taskDescription: {
          boxes: toDescriptionPayloadBoxes(node.task.descriptionBoundingBoxes),
          text: node.task.description,
        },
        additionalInformation:
          node.additionalInfoItems.length > 0
            ? {
                boxes: imageIdsAsBoxes(additionalInfoImageIds(node.additionalInfoItems)),
                text: formatAdditionalInfoText(node.additionalInfoItems),
              }
            : undefined,
      }));

      let results: Map<string, TaskExtractionSemanticResult>;
      try {
        results = await evaluateTaskTreeSemanticLLM(hierarchyInputs, targetIds, { onCost });
      } catch {
        results = new Map();
      }

      for (const node of nodes) {
        if (!node.isTarget) continue;

        const semanticResult = results.get(node.id) ?? { score: 1, reason: 'not_evaluated_fallback' };

        for (const box of node.task.descriptionBoundingBoxes ?? []) {
          if ((box as any)?.contentType === 'image') continue;

          const extractedText = String((box as any)?.ocrExtractedText ?? '').trim();
          const artifactDensity = calculateArtifactDensity(extractedText);
          const existingConfidence = clamp01(Number((box as any)?.confidence ?? 0));

          if (artifactDensity >= unreadableArtifactThreshold) {
            (box as any).confidence = 0;
          } else {
            (box as any).confidence = clamp01(existingConfidence * semanticResult.score);
          }
        }

        if (semanticResult.warningCodes?.length) {
          const existing = taskWarningCodes.get(node.task) ?? new Set<DescriptionWarningCode>();
          for (const code of semanticResult.warningCodes) existing.add(code);
          taskWarningCodes.set(node.task, existing);
        }
      }
    } catch (err) {
      logger.error({ err, context: 'applySemanticConfidenceToRecursiveTasks' }, 'Root task tree evaluation failed');
    }
  });

  for (const [task, codes] of taskWarningCodes) {
    (task as any).descriptionWarningCodes = [...codes];
  }

  return tasks;
}

export async function calculateTaskExtractionConfidence(
  params: CalculateTaskExtractionConfidenceParams,
): Promise<TaskExtractionConfidenceResult> {
  const {
    bbxConfidence,
    ocrConfidence,
    baseConfidenceOverride,
    extractedText,
    unreadableArtifactThreshold = DEFAULT_UNREADABLE_ARTIFACT_THRESHOLD,
  } = params;

  const baseConfidence =
    typeof baseConfidenceOverride === 'number'
      ? clamp01(baseConfidenceOverride)
      : calculateBaseExtractionConfidence(bbxConfidence, Number(ocrConfidence ?? 0));
  const artifactDensity = calculateArtifactDensity(extractedText);

  if (artifactDensity >= unreadableArtifactThreshold) {
    return {
      baseConfidence,
      finalConfidence: 0,
      artifactDensity,
      semanticScore: 0,
      semanticReason: `artifact_density_${artifactDensity.toFixed(3)}_above_${unreadableArtifactThreshold.toFixed(3)}`,
      skippedSemantic: true,
    };
  }

  return {
    baseConfidence,
    finalConfidence: baseConfidence,
    artifactDensity,
    semanticScore: 1,
    skippedSemantic: true,
  };
}
